import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { FilterOptions, FilterValues, OpsStreamType } from '../../models/filters.model';
import { ShiftLog, ShiftLogPost } from '../../models/shift-log.model';
import { ShiftLogsService } from '../../services/api/shift-logs.api.service';
import { ExportColumns, ExportCSVService } from '../../services/export-csv.service';
import { PaginationStore } from '../../services/stores/pagination.store';
import { SelectionStore } from '../../services/stores/selection.store';
import { UserStore } from '../../services/stores/user.store';
import { TableStateComponent } from '../../shared/components/table-state/table-state.component';
import { ShiftFiltersComponent } from './shift-filters/shift-filters.component';
import { ShiftTableComponent } from './shift-table/shift-table.component';
import { ShiftLogTaskDialog } from '../dialogs/task-dialogs/shiftlog-dialog/shiftlog-task.dialog';
import { ShiftLogTaskFormValue, TaskDialogData, TaskDialogMode, TaskDialogResult, TaskDialogResultAction } from '../dialogs/task-dialogs/task.dialog.model';
import { GenericDialogComponent, GenericDialogData } from '../dialogs/generic-dialog/generic-dialog.dialog';
import { ToastService } from '../../services/toastr.service';
import { NO_PROGRAM_ID, NO_PROGRAM_LABEL } from '../../shared/constants/program';

@Component({
  selector: 'dr-ops-stream-ui-shift-logs',
  imports: [ShiftTableComponent, ShiftFiltersComponent, TableStateComponent],
  templateUrl: './shift-logs.component.html',
  styleUrl: './shift-logs.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [PaginationStore, SelectionStore],
})
export class ShiftLogsComponent {
  private readonly shiftLogsService = inject(ShiftLogsService);
  private dialog = inject(Dialog);
  private readonly exportCSVService = inject(ExportCSVService);
  readonly paginationStore = inject(PaginationStore);
  readonly selectionStore = inject(SelectionStore);
  readonly userStore = inject(UserStore);
  private readonly destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);

  readonly allLogsSignal = signal<ShiftLog[]>([]);
  readonly isLoadingSignal = signal(true);

  readonly activeFiltersSignal = signal<FilterValues>({ activity: null, program: null, createdBy: null, dueDate: null, status: null });

  readonly filterOptionsSignal = computed<FilterOptions>(() => {
    const logs = this.allLogsSignal();
    const activities = new Set<string>();
    const programs = new Set<string>();
    const creators = new Set<string>();

    logs.forEach((log) => {
      activities.add(log.activity);
      programs.add(log.programName);
      creators.add(log.createdByName);
    });

    return {
      activities: [...activities].sort(),
      programs: [...programs].sort(),
      creators: [...creators].sort(),
    };
  });

  readonly filteredLogsSignal = computed(() => {
    const filters = this.activeFiltersSignal();
    const logs = this.allLogsSignal();
    return logs.filter((log) => this.matchesFilters(log, filters));
  });

  readonly selectedOpsStreamTypeSignal = computed<OpsStreamType>(() => {
    const filters = this.activeFiltersSignal();
    return filters.status ?? OpsStreamType.PENDING;
  });

  readonly showCreateButtonSignal = computed(() => {
    const selectedType = this.selectedOpsStreamTypeSignal();
    const selectedCount = this.selectionStore.selectedCountSignal();
    return selectedType === OpsStreamType.PENDING && selectedCount === 0;
  });

  readonly showResolveButtonSignal = computed(() => {
    const selectedType = this.selectedOpsStreamTypeSignal();
    const selectedCount = this.selectionStore.selectedCountSignal();
    return selectedType === OpsStreamType.PENDING && selectedCount > 0;
  });

  protected readonly exportCSVColumns: ExportColumns<ShiftLog> = Object.freeze([
    { header: 'LOG ID', accessor: (log: ShiftLog) => log.id },
    { header: 'PROGRAM', accessor: (log: ShiftLog) => log.programName },
    { header: 'CREATED BY', accessor: (log: ShiftLog) => log.createdByName },
    { header: 'DUE DATE', accessor: (log: ShiftLog) => log.dueDate },
    { header: 'NOTES', accessor: (log: ShiftLog) => log.notes },
  ]);

  private matchesFilters(log: ShiftLog, filters: FilterValues): boolean {
    // Early return for simple string filters
    if (filters.activity && log.activity !== filters.activity) return false;
    if (filters.program && log.programName !== filters.program) return false;
    if (filters.createdBy && log.createdByName !== filters.createdBy) return false;

    // Date comparison with early return
    if (filters.dueDate && !this.isSameDate(log.dueDate, filters.dueDate)) {
      return false;
    }

    return true;
  }

  private isSameDate(date1: string | Date, date2: string | Date): boolean {
    return moment.utc(date1).isSame(moment.utc(date2), 'day');
  }

  readonly pagedLogsSignal = computed(() => this.paginationStore.getPagedData(this.filteredLogsSignal()));
  readonly showTableSignal = computed(() => !this.isLoadingSignal() && this.filteredLogsSignal().length > 0);

  constructor() {
    effect(() => this.selectionStore.setTotalItems(this.filteredLogsSignal().map((log) => log.id)));
  }

  ngOnInit(): void {
    this.loadLogs();
  }

  onFiltersChange(filters: FilterValues): void {
    this.activeFiltersSignal.set({ ...filters });
    this.paginationStore.resetPageIndex(); // return to page 0 when filters change
    this.selectionStore.clearSelection(); // clear selected rows when filters change
  }

  private loadLogs(): void {
    this.isLoadingSignal.set(true);
    this.shiftLogsService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.allLogsSignal.set(data);
          this.isLoadingSignal.set(false);
        },
        error: () => {
          this.isLoadingSignal.set(false);
        },
      });
  }

  protected exportCSV() {
    this.exportCSVService.exportCSV(this.filteredLogsSignal, this.exportCSVColumns);
  }

  protected handleDeleteLogs() {
    const selectedIds = this.selectionStore.selectedIdsSignal();
    const isMultiple = selectedIds.size > 1;

    const dialogRef = this.dialog.open(GenericDialogComponent, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: isMultiple ? 'Delete Shift Logs?' : 'Delete Shift Log?',
        subTitle: isMultiple
          ? `Are you sure you want to delete the ${selectedIds.size} selected Shift Logs? This action is not reversible`
          : `Are you sure you want to delete the selected Shift Log? This action is not reversible`,
        onConfirmText: 'Confirm',
        onCancelText: 'Cancel',
        onCancel: () => dialogRef.close(false),
        onConfirm: () => dialogRef.close(true),
      } as GenericDialogData,
    });
    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => this.handleDeleteAlertsConfirmation(!!confirmed, selectedIds));
  }

  private handleDeleteAlertsConfirmation(confirmed: boolean | null, selectedIds: Set<string>) {
    if (!confirmed || selectedIds.size === 0) {
      return;
    }
    const userId = this.userStore.userIdSignal();
    if (!userId) {
      console.error('User not authenticated, cannot resolve logs');
      return;
    }

    const isMultiple = selectedIds.size > 1;

    this.shiftLogsService
      .removeMultiple(selectedIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (isMultiple) {
            this.toast.success('Entries deleted successfully', 'The selected entries have been removed from your list');
          } else {
            this.toast.success('Entry deleted successfully', 'The selected entry has been removed from your list');
          }
          this.selectionStore.clearSelection();
          this.loadLogs();
        },
        error: (err) => {
          console.error('Failed to delete selected logs', err);
        },
      });
  }

  handleView(log: ShiftLog) {
    this.dialog.open(ShiftLogTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'View Shift Log Activity',
        mode: TaskDialogMode.VIEW,
        defaults: this.logToTaskFormValue(log),
      } satisfies TaskDialogData,
    });
  }

  handleEdit(log: ShiftLog) {
    const ref = this.dialog.open(ShiftLogTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Edit Shift Log Activity',
        mode: TaskDialogMode.EDIT,
        log: log,
        defaults: this.logToTaskFormValue(log),
      } satisfies TaskDialogData,
    }) as DialogRef<TaskDialogResult<ShiftLog, ShiftLogTaskFormValue>>;

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result || result.action === TaskDialogResultAction.CANCEL) return;

      if (result.action === TaskDialogResultAction.UPDATE && !!result.inputLog)
        this.handleUpdateShiftLogResult(result.inputLog as ShiftLog, result.resultTaskFormValue as ShiftLogTaskFormValue);
    });
  }

  private handleUpdateShiftLogResult(log: ShiftLog, result: ShiftLogTaskFormValue) {
    if (!result) return;

    const currentUserId = this.userStore.userIdSignal();
    if (!currentUserId) {
      console.error('User not authenticated, cannot create log');
      return;
    }

    const logToUpdate = { ...log, ...result } as ShiftLog;

    const payload = this.shiftLogsService.buildUpdatedPayload(logToUpdate, currentUserId);

    this.updateShiftLog(log.id, payload);
  }

  private updateShiftLog(id: string, payload: ShiftLogPost) {
    this.shiftLogsService
      .update(id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toast.success('Entry updated successfully', 'The entry is now updated in your list.');
        this.loadLogs();
      });
  }

  private logToTaskFormValue(log: ShiftLog): Partial<ShiftLogTaskFormValue> {
    return {
      programId: log.programId === NO_PROGRAM_ID ? NO_PROGRAM_LABEL : (log.programId ?? ''),
      notes: log.notes ?? '',
      activity: log.activity ?? '',
      dueDate: this.isoToDatetimeLocal(log.dueDate),
    };
  }

  private isoToDatetimeLocal(value: string | Date | null | undefined): string | undefined {
    if (!value) return undefined;

    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return undefined;

    const pad = (n: number) => String(n).padStart(2, '0');

    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  }

  protected handleCreate() {
    const ref = this.dialog.open(ShiftLogTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Create Shift Log Activity',
        mode: TaskDialogMode.CREATE,
      } as TaskDialogData,
    }) as DialogRef<TaskDialogResult<ShiftLog, ShiftLogTaskFormValue>>;

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result || result.action === TaskDialogResultAction.CANCEL) return;

      if (result.action === TaskDialogResultAction.CREATE) this.handleCreateShiftLogResult(result.resultTaskFormValue as ShiftLogTaskFormValue);
    });
  }

  private handleCreateShiftLogResult(result: ShiftLogTaskFormValue) {
    if (!result) return;

    const userId = this.userStore.userIdSignal();
    if (!userId) {
      console.error('User not authenticated, cannot create log');
      return;
    }

    const model = this.shiftLogsService.createPayload(result, userId);

    this.sendCreateShiftLogRequest(model);
  }

  private sendCreateShiftLogRequest(model: ShiftLogPost) {
    this.shiftLogsService
      .create(model)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toast.success('Entry created successfully', 'The new entry is now in your list');
        this.loadLogs();
      });
  }
}
